import { surfaceKey } from "../../User/redux/ui-preferences.types.ts"

export interface CalendarNotificationPreferences {
  // Minutes before start a brand-new event's reminder defaults to — the
  // exact same value space as `Event.reminder_offset` itself (null = no
  // reminder), so this is a default for that field, never a second one.
  defaultReminderOffset: number | null
}

// 5 minutes matches the value `CalendarAddEventForm` already hardcoded for a
// fresh event before this setting existed — a user who never opens this
// section keeps today's exact behavior.
export const DEFAULT_CALENDAR_NOTIFICATION_PREFERENCES: CalendarNotificationPreferences = {
  defaultReminderOffset: 5
}

export const CALENDAR_NOTIFICATION_PREFERENCES_KEY = surfaceKey("calendar.notifications")

const isValidOffset = (value: unknown): value is number | null =>
  value === null || (typeof value === "number" && Number.isFinite(value))

/**
 * Same defensive-fill pattern as `normalizeWorkingSchedule` — a stored value
 * can predate a field this shape later gained, or simply be `unknown` off
 * the wire. No further validation than "a real number or null": the backend
 * already accepts any integer in [1, 43200] for `reminder_offset` itself
 * (see `CalendarAddReminderOffsetField`'s own comment), so this default is
 * exactly as permissive as the field it feeds.
 */
export function normalizeCalendarNotificationPreferences(
  raw: unknown
): CalendarNotificationPreferences {
  const input = (raw && typeof raw === "object" ? raw : {}) as Partial<CalendarNotificationPreferences>

  return {
    defaultReminderOffset: isValidOffset(input.defaultReminderOffset)
      ? input.defaultReminderOffset
      : DEFAULT_CALENDAR_NOTIFICATION_PREFERENCES.defaultReminderOffset
  }
}
